import React, {
  useContext, useEffect, useRef, useMemo,
} from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { HashLink } from 'react-router-hash-link'

import GlobalContext from 'agency/digital_renderer/utils/context'
import { validateRenderConditions } from 'functions/replaceTextShortCodes'
import { digObject, sortArrayBy } from 'functions/utilities'
import useOutsideClick from 'hooks/useOutsideClick'

import BrandLogo from 'components/BrandLogo'
import styles from './styles.scss'

const getSortedKeys = (dataSource, sections) => {
  const sortedSectionKeys = digObject(dataSource, 'options.sorted_section_keys', [])

  if (dataSource && sortedSectionKeys.length){
    const keyCountsMatch = sections.length === sortedSectionKeys.length
    if (keyCountsMatch) return sortedSectionKeys
  }

  const sorted = sortArrayBy(sections, 'asc', section => digObject(section, 'options.sort', 0))

  return sorted.map(section => section.id)
}

const sortBySortedKeys = (sortedKeys, sections) => {
  if (sortedKeys.length){
    return sortedKeys.map(key => sections.find(s => s.id === key))
  }

  return sections
}

const collectAnchors = (dataSource, defaultContext, sectionGroups, disabledSections) => {
  const anchors = []

  if (sectionGroups && sectionGroups.length){
    sectionGroups.forEach((group) => {
      const sections = group.sections || []
      const sortedKeys = getSortedKeys(dataSource, sections)
      const sortedSections = sortBySortedKeys(sortedKeys, sections)

      sortedSections.forEach((section) => {
        const anchor = digObject(section, 'options.anchor')
        const renderConditions = digObject(section, 'options.renderConditions')
        const shouldRender = validateRenderConditions(renderConditions, defaultContext)

        if (!shouldRender) return null

        if (anchor && !disabledSections.includes(section.id)){
          anchors.push({
            id: anchor,
            label: anchor.replace(/-/g, ' '),
          })
        }
      })
    })
  }

  return anchors
}

const renderLogo = (brand, { logoType }) => (
  <BrandLogo
    brand={brand}
    type={logoType || 'logo'}
    render={({ url }) => <span className={styles.logo} style={{ backgroundImage: `url(${url})` }} />}
  />
)

const Sidebar = (props) => {
  const {
    brand,
    callbacks: { toggleMenu },
    sectionGroups,
    showSidebar,
    sidebarOptions,
  } = props

  const defaultContext = useContext(GlobalContext)

  const {
    dataSource, loadedDataSource, updatingDataSource,
  } = defaultContext

  const disabledSections = digObject(dataSource, 'options.disabled_sections', [])

  const contentEl = useRef()
  const [isClickedOutside, setIsClickedOutside] = useOutsideClick(contentEl, { enabled: showSidebar })

  useEffect(() => {
    if (!showSidebar) setIsClickedOutside(false)
    if (isClickedOutside && showSidebar){
      toggleMenu()
      setIsClickedOutside(false)
    }
  }, [isClickedOutside, showSidebar])

  const anchors = useMemo(() => collectAnchors(dataSource, defaultContext, sectionGroups, disabledSections), [
    loadedDataSource,
    updatingDataSource,
  ])

  const { alignAnchor, anchorBorderBottom, style: sidebarStyle = {} } = sidebarOptions

  return (
    <div className={classnames(styles.root, showSidebar ? styles.show : null)} ref={contentEl} style={sidebarStyle}>
      {renderLogo(brand, sidebarOptions)}

      <ul className={styles.links}>
        {anchors.map(anchor => (
          <li key={anchor.id}>
            <HashLink
              smooth
              style={{ color: sidebarStyle.color, justifyContent: alignAnchor, borderBottom: anchorBorderBottom }}
              to={`#${anchor.id}`}
            >
              {anchor.label}
            </HashLink>
          </li>
        ))}
      </ul>
    </div>
  )
}

Sidebar.propTypes = {
  brand: PropTypes.object.isRequired,
  callbacks: PropTypes.shape({
    toggleMenu: PropTypes.func.isRequired,
  }).isRequired,
  sectionGroups: PropTypes.array.isRequired,
  showSidebar: PropTypes.bool,
  sidebarOptions: PropTypes.object,
}

Sidebar.defaultProps = {
  sidebarOptions: {},
}

export default Sidebar
